/**
 * DataService - Unified data layer for the new JSON schema
 *
 * Loads era-partitioned events, taxonomy, eras, and company entities.
 * Provides filtered views, search, and relationship traversal.
 */

// Import all data at build time via Vite's JSON import
import taxonomyData from '../../data/taxonomy/categories.json';
import erasData from '../../data/eras/eras.json';
import companiesData from '../../data/entities/companies/all_companies.json';

// Era event files - imported statically for Vite bundling
import eraFoundation from '../../data/events/1940-1984_foundation.json';
import eraPCRevolution from '../../data/events/1985-1994_pc_revolution.json';
import eraInternet from '../../data/events/1995-2000_internet.json';
import eraWeb2 from '../../data/events/2001-2010_web2_social.json';
import eraCloudMobile from '../../data/events/2011-2019_cloud_mobile.json';
import eraAIRevolution from '../../data/events/2020-2024_ai_revolution.json';
import eraAgenticAI from '../../data/events/2025-2030_agentic_ai.json';

const ERA_MODULES = [
  eraFoundation,
  eraPCRevolution,
  eraInternet,
  eraWeb2,
  eraCloudMobile,
  eraAIRevolution,
  eraAgenticAI,
];

class DataService {
  constructor() {
    this.events = [];
    this.categories = [];
    this.categoryMap = new Map();
    this.eras = [];
    this.companies = [];
    this.companyMap = new Map();
    this.eventMap = new Map();

    this._loaded = false;
  }

  load() {
    if (this._loaded) return;

    // Load categories
    this.categories = taxonomyData.categories;
    for (const cat of this.categories) {
      this.categoryMap.set(cat.id, cat);
    }

    // Load eras
    this.eras = erasData.eras;

    // Load companies
    this.companies = companiesData.companies;
    for (const com of this.companies) {
      this.companyMap.set(com.id, com);
    }

    // Load all events from era files
    for (const eraModule of ERA_MODULES) {
      if (eraModule?.events) {
        for (const evt of eraModule.events) {
          this.events.push(evt);
          this.eventMap.set(evt.id, evt);
        }
      }
    }

    // Sort events by date
    this.events.sort((a, b) => {
      const yd = a.date.year - b.date.year;
      return yd !== 0 ? yd : (a.date.month || 1) - (b.date.month || 1);
    });

    this._loaded = true;
    console.log(`DataService loaded: ${this.events.length} events, ${this.categories.length} categories, ${this.companies.length} companies, ${this.eras.length} eras`);
  }

  // Get all top-level categories (swimlanes)
  getTopCategories() {
    return this.categories.filter(c => c.parent_id === null);
  }

  // Get subcategories for a parent
  getSubcategories(parentId) {
    return this.categories.filter(c => c.parent_id === parentId);
  }

  // Get category with its parent chain
  getCategoryChain(categoryId) {
    const chain = [];
    let current = this.categoryMap.get(categoryId);
    while (current) {
      chain.unshift(current);
      current = current.parent_id ? this.categoryMap.get(current.parent_id) : null;
    }
    return chain;
  }

  // Get the top-level category for any category ID
  getTopCategory(categoryId) {
    let current = this.categoryMap.get(categoryId);
    while (current && current.parent_id) {
      current = this.categoryMap.get(current.parent_id);
    }
    return current;
  }

  // Get color for a category (falls back to parent)
  getCategoryColor(categoryId) {
    const cat = this.categoryMap.get(categoryId);
    if (cat) return cat.color;
    return '#888888';
  }

  // Filter events by criteria
  filterEvents({ categories, tags, eventTypes, yearRange, significance, search, companyId } = {}) {
    let filtered = this.events;

    if (categories && categories.length > 0) {
      // Include events matching category or any subcategory
      const expandedCats = new Set();
      for (const catId of categories) {
        expandedCats.add(catId);
        // Add all children
        for (const cat of this.categories) {
          if (cat.parent_id === catId) expandedCats.add(cat.id);
        }
      }
      filtered = filtered.filter(e => expandedCats.has(e.category));
    }

    if (tags && tags.length > 0) {
      filtered = filtered.filter(e => {
        const eventTags = e.tags || [];
        return tags.some(t => eventTags.includes(t));
      });
    }

    if (eventTypes && eventTypes.length > 0) {
      filtered = filtered.filter(e => eventTypes.includes(e.event_type));
    }

    if (yearRange) {
      const [min, max] = yearRange;
      filtered = filtered.filter(e => e.date.year >= min && e.date.year <= max);
    }

    if (significance) {
      filtered = filtered.filter(e => e.significance >= significance);
    }

    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(e =>
        e.title.toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.tags || []).some(t => t.includes(q))
      );
    }

    if (companyId) {
      filtered = filtered.filter(e =>
        (e.entities?.companies || []).includes(companyId)
      );
    }

    return filtered;
  }

  // Get events grouped by top-level category (for swimlane view)
  getEventsBySwimlane(filters = {}) {
    const events = this.filterEvents(filters);
    const lanes = new Map();

    for (const cat of this.getTopCategories()) {
      lanes.set(cat.id, { category: cat, events: [] });
    }

    for (const evt of events) {
      const topCat = this.getTopCategory(evt.category);
      if (topCat && lanes.has(topCat.id)) {
        lanes.get(topCat.id).events.push(evt);
      }
    }

    return lanes;
  }

  // Get related events for a given event
  getRelatedEvents(eventId) {
    const event = this.eventMap.get(eventId);
    if (!event || !event.relationships) return [];

    return event.relationships.map(rel => ({
      ...rel,
      event: this.eventMap.get(rel.target_id)
    })).filter(r => r.event);
  }

  // Get all events for a company
  getCompanyTimeline(companyId) {
    return this.events.filter(e =>
      (e.entities?.companies || []).includes(companyId)
    );
  }

  // Get the era for a given year
  getEraForYear(year) {
    return this.eras.find(e => year >= e.start_year && year <= e.end_year);
  }

  // Get all unique tags across events
  getAllTags() {
    const tags = new Set();
    for (const evt of this.events) {
      for (const tag of (evt.tags || [])) {
        tags.add(tag);
      }
    }
    return [...tags].sort();
  }

  // Get event density by year (for density chart)
  getEventDensity(filters = {}) {
    const events = this.filterEvents(filters);
    const density = {};
    for (const evt of events) {
      density[evt.date.year] = (density[evt.date.year] || 0) + 1;
    }
    return density;
  }

  // Convert to legacy format for backward compatibility with existing timeline.js
  toLegacyFormat() {
    const companyEvents = [];
    const technologyEvents = [];
    const aiMlEvents = [];

    for (const evt of this.events) {
      const topCat = this.getTopCategory(evt.category);
      const topCatId = topCat?.id || evt.category;
      const company = (evt.entities?.companies || [])
        .map(id => this.companyMap.get(id)?.name)
        .filter(Boolean)[0] || '';

      const legacyEvent = {
        id: evt._legacy_id || evt.id,
        year: String(evt.date.year),
        month: String(evt.date.month || ''),
        date: evt.date.month
          ? `${evt.date.year}-${String(evt.date.month).padStart(2, '0')}-01`
          : `${evt.date.year}-01-01`,
        title: evt.title,
        description: evt.description || '',
        category: topCatId,
        event_types: evt.event_type,
        valuation: String(evt.metrics?.valuation_usd || 0),
        source_url: evt.sources?.[0]?.url || '',
        significance: evt.significance
      };

      if (topCatId === 'model') {
        aiMlEvents.push({
          ...legacyEvent,
          organization: company,
          milestone_type: evt.tags?.[0] || evt.event_type,
          impact_score: String(evt.significance),
          parameters: String(evt.metrics?.parameters || 0)
        });
      } else if (topCatId === 'infrastructure' || topCatId === 'platform' || topCatId === 'data') {
        technologyEvents.push({
          ...legacyEvent,
          technology: evt.title,
          institution: company,
          impact_score: String(evt.significance)
        });
      } else {
        companyEvents.push({
          ...legacyEvent,
          company
        });
      }
    }

    return {
      companyEvents,
      technologyEvents,
      aiMlEvents,
      categories: this.categories.map(c => ({
        id: c.id,
        name: c.name,
        color_hex: c.color,
        parent: c.parent_id || '',
        description: c.description,
        icon: c.icon
      })),
      eras: this.eras.map(e => ({
        id: e.id,
        name: e.name,
        start_year: String(e.start_year),
        end_year: String(e.end_year),
        color_hex: e.color,
        intensity: String(e.intensity),
        description: e.description
      }))
    };
  }
}

// Singleton instance
const dataService = new DataService();

export { DataService };
export default dataService;
